import React, { createContext, useContext, useState, useCallback } from 'react';

import * as GQL from '~/graphql/generated/client';

/**
 * Assessment Progress Context
 *
 * Provides real-time progress updates for assessments and bundles without
 * triggering expensive ProjectFromId queries.
 *
 * This context maintains "live" progress data that gets updated immediately
 * from mutation responses, allowing progress bars and counters to update
 * instantly while cache updates use broadcast: false.
 */

interface BundleProgress {
  completionPercent: number;
  numAnswers: number;
  numControls: number;
}

interface AssessmentProgressState {
  // Assessment-level progress
  assessmentCompletion: Record<string, number>; // assessmentId -> completionPercent

  // Bundle-level progress
  bundleProgress: Record<string, BundleProgress>; // bundleId -> progress data

  // Control-level answer status for instant UI updates
  answerStatuses: Record<string, GQL.AnswerStatus | null>; // controlId -> answerStatus
}

interface AssessmentProgressContextValue {
  state: AssessmentProgressState;

  /**
   * Update assessment completion percentage
   */
  updateAssessmentCompletion: (
    assessmentId: string,
    completionPercent: number
  ) => void;

  /**
   * Update bundle progress (completion % and answer count)
   */
  updateBundleProgress: (
    bundleId: string,
    completionPercent: number,
    numAnswers: number,
    numControls: number
  ) => void;

  /**
   * Update answer status for a specific control (instant UI feedback)
   */
  updateAnswerStatus: (
    controlId: string,
    answerStatus: GQL.AnswerStatus | null
  ) => void;

  /**
   * Get assessment completion percentage (returns undefined if not in state)
   */
  getAssessmentCompletion: (assessmentId: string) => number | undefined;

  /**
   * Get bundle progress (returns undefined if not in state)
   */
  getBundleProgress: (bundleId: string) => BundleProgress | undefined;

  /**
   * Get answer status for a control (returns undefined if not in state)
   */
  getAnswerStatus: (controlId: string) => GQL.AnswerStatus | null | undefined;

  /**
   * Remove bundle progress entry so the left nav falls back to Apollo cache values.
   * Call on mutation error to revert optimistic counts.
   */
  removeBundleProgress: (bundleId: string) => void;

  /**
   * Clear all progress data for a specific assessment (cleanup on unmount).
   * @param assessmentId - The assessment ID (keys are formatted as "assessmentId:controlIdentifier")
   * @param bundleIds - Optional array of bundle IDs to clean up from bundleProgress
   */
  clearAssessmentStatuses: (assessmentId: string, bundleIds?: string[]) => void;
}

const AssessmentProgressContext = createContext<
  AssessmentProgressContextValue | undefined
>(undefined);

interface AssessmentProgressProviderProps {
  children: React.ReactNode;
}

export const AssessmentProgressProvider: React.FC<
  AssessmentProgressProviderProps
> = ({ children }) => {
  const [state, setState] = useState<AssessmentProgressState>({
    assessmentCompletion: {},
    bundleProgress: {},
    answerStatuses: {},
  });

  const updateAssessmentCompletion = useCallback(
    (assessmentId: string, completionPercent: number) => {
      setState((prev) => ({
        ...prev,
        assessmentCompletion: {
          ...prev.assessmentCompletion,
          [assessmentId]: completionPercent,
        },
      }));
    },
    []
  );

  const updateBundleProgress = useCallback(
    (
      bundleId: string,
      completionPercent: number,
      numAnswers: number,
      numControls: number
    ) => {
      setState((prev) => ({
        ...prev,
        bundleProgress: {
          ...prev.bundleProgress,
          [bundleId]: {
            completionPercent,
            numAnswers,
            numControls,
          },
        },
      }));
    },
    []
  );

  const updateAnswerStatus = useCallback(
    (controlId: string, answerStatus: GQL.AnswerStatus | null) => {
      setState((prev) => ({
        ...prev,
        answerStatuses: {
          ...prev.answerStatuses,
          [controlId]: answerStatus,
        },
      }));
    },
    []
  );

  const getAssessmentCompletion = useCallback(
    (assessmentId: string) => {
      return state.assessmentCompletion[assessmentId];
    },
    [state.assessmentCompletion]
  );

  const getBundleProgress = useCallback(
    (bundleId: string) => {
      return state.bundleProgress[bundleId];
    },
    [state.bundleProgress]
  );

  const getAnswerStatus = useCallback(
    (controlId: string) => {
      return state.answerStatuses[controlId];
    },
    [state.answerStatuses]
  );

  const removeBundleProgress = useCallback((bundleId: string) => {
    setState((prev) => {
      const { [bundleId]: _, ...rest } = prev.bundleProgress;
      return { ...prev, bundleProgress: rest };
    });
  }, []);

  /**
   * Clear all progress data for a specific assessment.
   */
  const clearAssessmentStatuses = useCallback(
    (assessmentId: string, bundleIds?: string[]) => {
      if (!assessmentId) {
        return;
      }

      const prefix = `${assessmentId}:`;

      setState((prev) => {
        // Filter out answer statuses that belong to this assessment
        const filteredStatuses = Object.keys(prev.answerStatuses)
          .filter((key) => !key.startsWith(prefix))
          .reduce(
            (acc, key) => {
              acc[key] = prev.answerStatuses[key];
              return acc;
            },
            {} as Record<string, GQL.AnswerStatus | null>
          );

        // Filter out bundle progress for the provided bundleIds
        const filteredBundleProgress = bundleIds?.length
          ? Object.keys(prev.bundleProgress)
              .filter((bundleId) => !bundleIds.includes(bundleId))
              .reduce(
                (acc, bundleId) => {
                  acc[bundleId] = prev.bundleProgress[bundleId];
                  return acc;
                },
                {} as Record<string, BundleProgress>
              )
          : prev.bundleProgress;

        // Clean up assessment completion for this assessment
        const { [assessmentId]: _removed, ...remainingCompletion } =
          prev.assessmentCompletion;

        return {
          ...prev,
          answerStatuses: filteredStatuses,
          bundleProgress: filteredBundleProgress,
          assessmentCompletion: remainingCompletion,
        };
      });
    },
    []
  );

  const value: AssessmentProgressContextValue = {
    state,
    updateAssessmentCompletion,
    updateBundleProgress,
    removeBundleProgress,
    updateAnswerStatus,
    getAssessmentCompletion,
    getBundleProgress,
    getAnswerStatus,
    clearAssessmentStatuses,
  };

  return (
    <AssessmentProgressContext.Provider value={value}>
      {children}
    </AssessmentProgressContext.Provider>
  );
};

/**
 * Hook to access the assessment progress context
 */
export const useAssessmentProgress = (): AssessmentProgressContextValue => {
  const context = useContext(AssessmentProgressContext);
  if (!context) {
    throw new Error(
      'useAssessmentProgress must be used within AssessmentProgressProvider'
    );
  }
  return context;
};

/**
 * Optional hook that provides safe access (returns null if provider not available)
 * Useful for components that might be used outside the assessment context
 */
export const useAssessmentProgressOptional =
  (): AssessmentProgressContextValue | null => {
    const context = useContext(AssessmentProgressContext);
    return context ?? null;
  };
